import { get } from "lodash";
import React, { useState, useEffect } from "react";
import moment from "moment";
import { Tooltip } from "primereact/tooltip";
import { Title } from "components/Title";
import useLocalStore from "hooks/useLocalStore";
import useApp from "hooks/useApp";
import useApi from "hooks/useApi";
import { useTranslation } from "react-i18next";
import useAnimatedLoadingText from "hooks/useLoadingText";
import usePostForm from "hooks/usePostForm";

const LicensePage = (props) => {
  const localStore = useLocalStore();
  const app = useApp();
  const { t } = useTranslation();
  const api = useApi();
  const { isLicenseKeyValid, validateLicense } = usePostForm({});
  const { licenseData } = app;
  const [copyIconClass, setCopyIconClass] = useState("pi pi-clone");
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [organisationsLoading, setOrganisationsLoading] = useState(true);
  const MODULES = t("MODULES");
  const FACILITIES = t("FACILITIES");
  const LICENSE = t("LICENSE");
  const KEY = t("KEY");
  const ATTRIBUTES = t("ATTRIBUTES");
  const EXPIRY = t("EXPIRY");
  const CURRENT = t("CURRENT");
  const TOTAL = t("TOTAL");
  const USERS = t("USERS");
  useEffect(() => {
    Promise.allSettled([
      api.get("users/index"),
      api.get("modules/get_all_modules"),
      api.get("org/get_all_orgs"),
    ])
      .then((results) => {
        const usersResp = results[0];
        const modulesResp = results[1];
        const orgsResp = results[2];
        if (usersResp.status === "fulfilled") {
          const usersData = get(usersResp, "value.data.data", []);
          setUsers(usersData);
        } else {
          setUsers([]);
        }
        setUsersLoading(false);
        if (modulesResp.status === "fulfilled") {
          const modulesData = get(modulesResp, "value.data.data", []);
          setModules(modulesData);
        } else {
          setModules([]);
        }
        setModulesLoading(false);
        if (orgsResp.status === "fulfilled") {
          const orgsData = get(orgsResp, "value.data.data", []);
          setOrganisations(orgsData);
        } else {
          setOrganisations([]);
        }
        setOrganisationsLoading(false);
      })
      .catch((e) => {
        setUsers([]);
        setModules([]);
        setOrganisations([]);
        setUsersLoading(false);
        setModulesLoading(false);
        setOrganisationsLoading(false);
      });
  }, []);
  const animatedLoadingText = useAnimatedLoadingText(t("LOADING"), 500);
  useEffect(() => {
    validateLicense(null);
  }, []);

  return (
    <div>
      <main id="LicensePage" className="main-page">
        <section className="page-section mb-3">
          <div className="container">
            <div className="grid align-items-center">
              <div className="col ">
                <Title
                  title={LICENSE}
                  titleClass="text-2xl text-primary font-bold"
                  subTitleClass="text-900"
                  separator={false}
                />
              </div>
            </div>
          </div>
        </section>
        <section className="page-section ">
          <div className="container">
            <div className="grid ">
              <div className="col comp-grid">
                <div
                  className="card"
                  style={{
                    height: "340px",
                  }}
                >
                  <div className="mb-3 grid justify-content-start">
                    <div className="col-12">
                      <Title
                        title={KEY}
                        titleClass="text-xl"
                        subTitleClass="text-900"
                        separator={false}
                      />
                      <Tooltip
                        target=".license-key"
                        content={licenseData.key}
                        position="top"
                      />
                      <div
                        className="flex align-items-center"
                        style={{
                          position: "relative",
                        }}
                      >
                        <span
                          className="text-900 text-xl font-medium mb-1 card license-key"
                          style={{
                            backgroundColor: "var(--surface-200)",
                          }}
                        >
                          {licenseData.key||animatedLoadingText}
                        </span>
                        <i
                          className={copyIconClass}
                          style={{
                            fontSize: "2.5rem",
                            marginLeft: "10px",
                            color: "var(--gray-400)",
                            cursor: "pointer",
                            position: "absolute",
                            right: 0,
                            top: "15px",
                          }}
                          onClick={() => {
                            navigator.clipboard.writeText(licenseData.key);
                            setCopyIconClass("pi pi-check");
                            setTimeout(
                              () => setCopyIconClass("pi pi-clone"),
                              1000
                            );
                          }}
                        />
                      </div>
                    </div>
                    <div
                      className="col-12"
                      style={{
                        position: "relative",
                        top: "75px",
                      }}
                    >
                      <div className="mb-4">
                        <Title
                          title={ATTRIBUTES}
                          titleClass="text-xl"
                          subTitleClass="text-900"
                          separator={false}
                        />
                        <div>
                          <div className="mb-2">
                            <span className="text-400 font-medium">ID</span>
                            <span
                              className="font-bold"
                              style={{
                                marginLeft: "20px",
                              }}
                            >
                              {licenseData.id}
                            </span>
                          </div>
                          <div className="mb-2">
                            <span className="text-400 font-medium">
                              {EXPIRY}
                            </span>
                            <span
                              className="font-bold"
                              style={{
                                marginLeft: "20px",
                              }}
                            >
                              {moment(licenseData.expiry).format("DD/MM/YYYY")}
                            </span>
                          </div>
                          <div className="mb-2">
                            <span className="text-400 font-medium">
                              {t("STATUS")}
                            </span>
                            <span
                              className="font-bold"
                              style={{
                                marginLeft: "20px",
                              }}
                            >
                              {licenseData.status}
                            </span>
                            {licenseData.status === "EXPIRED" ||
                            licenseData.status === "EXPIRING" ? (
                              <span
                                className="font-bold p-error"
                                style={{
                                  marginLeft: "20px",
                                }}
                              >
                                {t("PLEASE_CONTACT_ADMINISTRATOR")}
                              </span>
                            ) : null}
                          </div>
                          <div className="mb-2">
                            <span className="text-400 font-medium">
                              {MODULES}
                            </span>
                            <span
                              className="font-bold"
                              style={{
                                marginLeft: "20px",
                              }}
                            >
                              {modulesLoading
                                ? animatedLoadingText
                                : `${modules.length} (${CURRENT}) / ${licenseData.metadata?.modules} (${TOTAL})`}
                            </span>
                            {modules.length >= licenseData.metadata?.modules &&
                            !modulesLoading ? (
                              <span
                                className="font-bold p-error"
                                style={{
                                  marginLeft: "20px",
                                }}
                              >
                                {t("PLEASE_CONTACT_ADMINISTRATOR")}
                              </span>
                            ) : null}
                          </div>
                          <div className="mb-2">
                            <span className="text-400 font-medium">
                              {FACILITIES}
                            </span>
                            <span
                              className="font-bold"
                              style={{
                                marginLeft: "20px",
                              }}
                            >
                              {organisationsLoading
                                ? animatedLoadingText
                                : `${organisations.length} (${CURRENT}) / ${licenseData.metadata?.orgs} (${TOTAL})`}
                            </span>
                            {organisations.length >=
                              licenseData.metadata?.orgs &&
                            !organisationsLoading ? (
                              <span
                                className="font-bold p-error"
                                style={{
                                  marginLeft: "20px",
                                }}
                              >
                                {t("PLEASE_CONTACT_ADMINISTRATOR")}
                              </span>
                            ) : null}
                          </div>
                          {licenseData.metadata?.users ? (
                            <div>
                              <span className="text-400 font-medium">
                                {USERS}
                              </span>
                              <span
                                className="font-bold"
                                style={{
                                  marginLeft: "20px",
                                }}
                              >
                                {usersLoading
                                  ? animatedLoadingText
                                  : `${users.length - 1} (${CURRENT}) / ${
                                      licenseData.metadata?.users
                                    } (${TOTAL})`}
                              </span>
                              {users.length - 1 >=
                                licenseData.metadata?.users && !usersLoading ? (
                                <span
                                  className="font-bold p-error"
                                  style={{
                                    marginLeft: "20px",
                                  }}
                                >
                                  {t("PLEASE_CONTACT_ADMINISTRATOR")}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LicensePage;
